import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Clock, Users, MapPin, MessageCircle, FileText, CalendarClock, Wallet } from "lucide-react";
import Seo from "../components/Seo";
import InquiryForm from "../components/InquiryForm";
import { useSite } from "../components/SiteContext";
import {
  Breadcrumbs, EditorialCard, FaqList, Gallery, Grid, Img, Loading, OverlayCard, PageHero,
  Reviews, RichText, Section, SectionHead,
} from "../components/shared";
import { getItem, getList, destUrl, inr, waLink } from "../lib/api";
import { fadeUp } from "../lib/motion";

function useDoc(collection, slug) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setDoc(null);
    setError(false);
    getItem(collection, slug).then(setDoc).catch(() => setError(true));
    window.scrollTo({ top: 0 });
  }, [collection, slug]);
  return { doc, error };
}

const NotFoundBlock = ({ backTo, label }) => (
  <Section testId="detail-not-found">
    <Seo title="Page Not Found | Azulo Travel" description="This travel page is unavailable. Browse our current destinations and journeys." noIndex />
    <h1 className="font-display text-4xl text-[var(--ink)]">Page not found</h1>
    <Link to={backTo} className="mt-4 inline-block text-[var(--brass-2)]">
      {label}
    </Link>
  </Section>
);

const Block = ({ title, eyebrow, children, id }) => (
  <div id={id} className="scroll-mt-28 border-t border-[var(--line)] pt-10">
    {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
    <h2 className="font-display text-[1.85rem] leading-tight text-[var(--ink)]">{title}</h2>
    <div className="mt-5">{children}</div>
  </div>
);

const TagList = ({ items, positive = true, testId }) => (
  <ul data-testid={testId} className="space-y-2.5">
    {(items || []).map((t, i) => (
      <li key={typeof t === "string" ? t : t.title || i} className="flex gap-3 text-[0.9rem] leading-relaxed text-[var(--ink-2)]">
        {positive ? (
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1F6F5B]" />
        ) : (
          <X className="mt-0.5 h-4 w-4 shrink-0 text-[#B42318]" />
        )}
        {t}
      </li>
    ))}
  </ul>
);

/* ---------------------------------------------------------------- EXPERIENCE */
export function ExperienceDetail() {
  const { slug } = useParams();
  const { settings } = useSite();
  const { doc: e, error } = useDoc("experiences", slug);
  const [more, setMore] = useState([]);

  useEffect(() => {
    if (e?.categories?.length)
      getList("experiences", { category: e.categories[0], limit: 4 })
        .then((r) => setMore((r.items || []).filter((x) => x.slug !== e.slug).slice(0, 3)))
        .catch(() => {});
  }, [e]);

  if (error) return <NotFoundBlock backTo="/experiences" label="Browse all experiences" />;
  if (!e) return <Loading label="Loading experience" />;

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Experiences", to: "/experiences" },
    { label: e.title },
  ];

  return (
    <>
      <Seo
        title={e.meta_title || `${e.title} | Azulo Travel`}
        description={e.meta_description || e.tagline}
        image={e.og_image || e.hero_image}
        path={`/experiences/${e.slug}`}
        record={e}
        keywords={e.keywords || []}
        noIndex={e.no_index}
        breadcrumbs={crumbs}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Private travel experience",
          name: e.title,
          description: e.meta_description || e.tagline,
          image: e.hero_image,
          areaServed: { "@type": "Place", name: e.city },
        }}
      />
      <PageHero image={e.hero_image} eyebrow={e.city} title={e.seo_heading || e.title} subtitle={e.tagline}>
        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/85">
          <span className="flex items-center gap-2" data-testid="experience-duration">
            <Clock className="h-4 w-4" /> {e.duration}
          </span>
          <span className="flex items-center gap-2" data-testid="experience-group">
            <Users className="h-4 w-4" /> {e.group_size}
          </span>
          <span className="flex items-center gap-2" data-testid="experience-price">
            <Wallet className="h-4 w-4" /> {inr(e.price_inr)}
          </span>
        </div>
      </PageHero>

      <Section testId="experience-detail">
        <Breadcrumbs items={crumbs} />
        <div className="grid gap-12 lg:grid-cols-[1.55fr_1fr] lg:gap-16">
          <div className="min-w-0 space-y-10">
            <div>
              <p className="eyebrow mb-3">About this experience</p>
              <RichText text={e.overview} />
            </div>

            {e.highlights?.length > 0 && (
              <Block eyebrow="Highlights" title="What makes it special">
                <TagList items={e.highlights} testId="experience-highlights" />
              </Block>
            )}

            <Block eyebrow="The detail" title="Inclusions and exclusions">
              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <p className="mb-4 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                    Included
                  </p>
                  <TagList items={e.inclusions} testId="experience-inclusions" />
                </div>
                <div>
                  <p className="mb-4 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                    Not included
                  </p>
                  <TagList items={e.exclusions} positive={false} testId="experience-exclusions" />
                </div>
              </div>
            </Block>

            {e.pickup_info && (
              <Block eyebrow="Logistics" title="Pickup information">
                <div
                  data-testid="experience-pickup"
                  className="rounded-xl border border-[var(--line)] bg-[var(--surface-warm)] p-5"
                >
                  <p className="text-[0.9rem] leading-relaxed text-[var(--ink-2)]">{e.pickup_info}</p>
                </div>
              </Block>
            )}

            {e.map_query && (
              <Block eyebrow="Location" title="Where it happens">
                <div className="overflow-hidden rounded-xl border border-[var(--line)]">
                  <iframe
                    title={`Map of ${e.map_query}`}
                    data-testid="experience-map"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(e.map_query)}&output=embed`}
                    className="h-[320px] w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <p className="mt-3 flex items-center gap-2 text-xs text-[var(--muted-ink)]">
                  <MapPin className="h-3.5 w-3.5 text-[var(--brass)]" /> {e.map_query}
                </p>
              </Block>
            )}

            {e.gallery?.length > 0 && (
              <Block eyebrow="Gallery" title="In pictures">
                <Gallery images={e.gallery} alt={e.title} testId="experience-gallery" />
              </Block>
            )}

            {e.faqs?.length > 0 && (
              <Block eyebrow="Questions" title="Frequently asked">
                <FaqList faqs={e.faqs} testId="experience-faq" />
              </Block>
            )}

            {e.reviews?.length > 0 && (
              <Block eyebrow="Guest stories" title="Reviews">
                <Reviews reviews={e.reviews} testId="experience-review" />
              </Block>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <div className="mb-4 rounded-2xl border border-[var(--line)] bg-[var(--surface-warm)] p-6">
              <p className="eyebrow mb-2">Pricing</p>
              <p data-testid="experience-price-large" className="font-display text-3xl text-[var(--ink)]">
                {inr(e.price_inr)}
              </p>
              <p className="mt-1 text-xs text-[var(--muted-ink)]">{e.price_note}</p>
              <div className="mt-4 space-y-2 text-xs text-[var(--muted-ink)]">
                <p className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-[var(--brass)]" /> {e.duration}
                </p>
                <p className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-[var(--brass)]" /> {e.group_size}
                </p>
                {e.languages?.length > 0 && (
                  <p className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-[var(--brass)]" /> {e.languages.join(", ")}
                  </p>
                )}
              </div>
            </div>
            <InquiryForm
              source="experience"
              sourceRef={e.slug}
              sourceTitle={e.title}
              destination={e.city}
              compact
              title="Enquire about this experience"
              subtitle="We confirm availability within one business day."
            />
            <a
              href={waLink(settings.whatsapp, `Hello Azulo Travel, I am interested in "${e.title}" in ${e.city}.`)}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="experience-whatsapp-button"
              className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-5 py-3.5 text-sm font-semibold text-[var(--ink)] hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp us
            </a>
          </aside>
        </div>
      </Section>

      {more.length > 0 && (
        <Section wash testId="related-experiences">
          <SectionHead eyebrow="More like this" title="Related experiences" />
          <Grid cols={3}>
            {more.map((m) => (
              <EditorialCard
                key={m.slug}
                to={`/experiences/${m.slug}`}
                image={m.card_image || m.hero_image}
                title={m.title}
                meta={`${m.city} · ${m.duration}`}
                subtitle={m.tagline}
                testId={`related-experience-${m.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- JOURNEY */
export function JourneyDetail() {
  const { slug } = useParams();
  const { settings } = useSite();
  const { doc: j, error } = useDoc("journeys", slug);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (j?.related?.length)
      getList("journeys", { slugs: j.related.join(",") })
        .then((r) => setRelated(r.items || []))
        .catch(() => {});
  }, [j]);

  if (error) return <NotFoundBlock backTo="/journeys" label="Browse all journeys" />;
  if (!j) return <Loading label="Loading journey" />;

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Signature Journeys", to: "/journeys" },
    { label: j.title },
  ];

  return (
    <>
      <Seo
        title={j.meta_title || `${j.title} | Azulo Travel`}
        description={j.meta_description || j.tagline}
        image={j.og_image || j.hero_image}
        path={`/journeys/${j.slug}`}
        record={j}
        keywords={j.keywords || []}
        noIndex={j.no_index}
        breadcrumbs={crumbs}
        schema={{
          "@context": "https://schema.org",
          "@type": "TouristTrip",
          name: j.title,
          description: j.meta_description || j.tagline,
          image: j.hero_image,
          itinerary: {
            "@type": "ItemList",
            itemListElement: (j.itinerary || []).map((it, index) => ({
              "@type": "ListItem", position: index + 1,
              item: { "@type": "Place", name: it.title, description: it.detail },
            })),
          },
          offers: j.price_inr
            ? { "@type": "Offer", price: j.price_inr, priceCurrency: "INR" }
            : undefined,
        }}
      />
      <PageHero
        image={j.hero_image}
        eyebrow={`${j.collection_name || "Signature Journey"} · ${(j.countries || []).join(", ")}`}
        title={j.seo_heading || j.title}
        subtitle={j.tagline}
      >
        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/85">
          <span data-testid="journey-nights">{j.nights} nights</span>
          <span data-testid="journey-price">From {inr(j.price_inr)} per person</span>
          {j.best_months?.length > 0 && (
            <span data-testid="journey-months">Best: {j.best_months.join(", ")}</span>
          )}
        </div>
      </PageHero>

      <Section testId="journey-detail">
        <Breadcrumbs items={crumbs} />
        <div className="grid gap-12 lg:grid-cols-[1.55fr_1fr] lg:gap-16">
          <div className="min-w-0 space-y-10">
            <div>
              <p className="eyebrow mb-3">The journey</p>
              <RichText text={j.overview} />
            </div>

            {j.highlights?.length > 0 && (
              <Block eyebrow="Highlights" title="What is built in">
                <TagList items={j.highlights} testId="journey-highlights" />
              </Block>
            )}

            {j.itinerary?.length > 0 && (
              <Block eyebrow="Day by day" title="The route" id="itinerary">
                <ol data-testid="journey-itinerary">
                  {j.itinerary.map((it, i) => (
                    <li
                      key={it.day ?? i}
                      data-testid={`journey-day-${i}`}
                      className="flex gap-5 border-b border-[var(--line)] py-5 last:border-0"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(176,141,87,0.14)] text-xs font-bold text-[var(--brass-2)]">
                        {it.day}
                      </span>
                      <div>
                        <h3 className="font-display text-lg text-[var(--ink)]">{it.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-[var(--muted-ink)]">{it.detail}</p>
                        {it.stay && it.stay !== "—" && (
                          <p className="mt-1.5 text-xs font-semibold text-[var(--brass-2)]">
                            Stay: {it.stay}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </Block>
            )}

            {j.hotels?.length > 0 && (
              <Block eyebrow="Where you stay" title="The properties">
                <div data-testid="journey-hotels" className="space-y-4">
                  {j.hotels.map((h, i) => (
                    <div
                      key={h.name || i}
                      className="flex flex-col gap-4 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface)] sm:flex-row"
                    >
                      <div className="sm:w-52 sm:shrink-0">
                        <Img src={h.image} alt={h.name} ratio="4/3" />
                      </div>
                      <div className="p-5 sm:pl-0">
                        <p className="eyebrow mb-1.5">
                          {h.city} &middot; {h.category}
                        </p>
                        <h3 className="font-display text-xl text-[var(--ink)]">{h.name}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-[var(--muted-ink)]">{h.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Block>
            )}

            <Block eyebrow="The detail" title="Inclusions and exclusions">
              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <p className="mb-4 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                    Included
                  </p>
                  <TagList items={j.inclusions} testId="journey-inclusions" />
                </div>
                <div>
                  <p className="mb-4 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                    Not included
                  </p>
                  <TagList items={j.exclusions} positive={false} testId="journey-exclusions" />
                </div>
              </div>
            </Block>

            {j.gallery?.length > 0 && (
              <Block eyebrow="Gallery" title="In pictures">
                <Gallery images={j.gallery} alt={j.title} testId="journey-gallery" />
              </Block>
            )}

            {j.faqs?.length > 0 && (
              <Block eyebrow="Questions" title="Frequently asked">
                <FaqList faqs={j.faqs} testId="journey-faq" />
              </Block>
            )}

            {j.reviews?.length > 0 && (
              <Block eyebrow="Guest stories" title="Travellers on this journey">
                <Reviews reviews={j.reviews} testId="journey-review" />
              </Block>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <div className="mb-4 rounded-2xl border border-[var(--line)] bg-[var(--surface-warm)] p-6">
              <p className="eyebrow mb-2">From</p>
              <p className="font-display text-3xl text-[var(--ink)]">{inr(j.price_inr)}</p>
              <p className="mt-1 text-xs text-[var(--muted-ink)]">
                per person, twin sharing &middot; {j.nights} nights
              </p>
              {j.best_months?.length > 0 && (
                <p className="mt-4 flex items-start gap-2 text-xs text-[var(--muted-ink)]">
                  <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--brass)]" />
                  Best months: {j.best_months.join(", ")}
                </p>
              )}
            </div>
            <InquiryForm
              source="journey"
              sourceRef={j.slug}
              sourceTitle={j.title}
              destination={(j.countries || [])[0] || j.title}
              title="Enquire about this journey"
              subtitle="Every departure is private. We will tailor it to your dates."
            />
            <a
              href={waLink(settings.whatsapp, `Hello Azulo Travel, I am interested in the ${j.title} journey.`)}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="journey-whatsapp-button"
              className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-5 py-3.5 text-sm font-semibold text-[var(--ink)] hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp us
            </a>
          </aside>
        </div>
      </Section>

      {related.length > 0 && (
        <Section wash testId="related-journeys">
          <SectionHead eyebrow="You may also like" title="Related journeys" />
          <Grid cols={3}>
            {related.map((r) => (
              <OverlayCard
                key={r.slug}
                to={`/journeys/${r.slug}`}
                image={r.card_image || r.hero_image}
                title={r.title}
                meta={`${r.nights} nights`}
                subtitle={r.tagline}
                testId={`related-journey-${r.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- TRAVEL STYLE */
export function TravelStyleDetail() {
  const { slug } = useParams();
  const { doc: s, error } = useDoc("travel_styles", slug);
  const [dests, setDests] = useState([]);
  const [journeys, setJourneys] = useState([]);

  useEffect(() => {
    if (!s) return;
    if (s.destination_slugs?.length)
      getList("destinations", { slugs: s.destination_slugs.join(",") })
        .then((r) => setDests(r.items || []))
        .catch(() => {});
    if (s.journey_slugs?.length)
      getList("journeys", { slugs: s.journey_slugs.join(",") })
        .then((r) => setJourneys(r.items || []))
        .catch(() => {});
  }, [s]);

  if (error) return <NotFoundBlock backTo="/travel-styles" label="All travel styles" />;
  if (!s) return <Loading label="Loading travel style" />;

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Travel Styles", to: "/travel-styles" },
    { label: s.title },
  ];

  return (
    <>
      <Seo
        title={s.meta_title || `${s.title} Travel | Azulo Travel`}
        description={s.meta_description || s.tagline}
        image={s.og_image || s.hero_image}
        path={`/travel-styles/${s.slug}`}
        record={s}
        keywords={s.keywords || []}
        breadcrumbs={crumbs}
      />
      <PageHero image={s.hero_image} eyebrow="Travel Style" title={s.seo_heading || s.title} subtitle={s.tagline} />
      <Section testId="travel-style-detail">
        <Breadcrumbs items={crumbs} />
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-16">
          <div className="min-w-0">
            <RichText text={s.overview} />
            {s.highlights?.length > 0 && (
              <div className="mt-10">
                <p className="eyebrow mb-4">How we design it</p>
                <TagList items={s.highlights} testId="style-highlights" />
              </div>
            )}
            {s.faqs?.length > 0 && (
              <div className="mt-10">
                <FaqList faqs={s.faqs} testId="style-faq" />
              </div>
            )}
          </div>
          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <InquiryForm
              source="travel_style"
              sourceRef={s.slug}
              sourceTitle={s.title}
              title={`Plan a ${s.title.toLowerCase()} journey`}
            />
          </aside>
        </div>
      </Section>

      {dests.length > 0 && (
        <Section wash testId="style-destinations">
          <SectionHead eyebrow="Where it works best" title="Recommended destinations" />
          <Grid cols={4}>
            {dests.map((d) => (
              <EditorialCard
                key={d.slug}
                to={destUrl(d)}
                image={d.card_image || d.hero_image}
                title={d.title}
                meta={d.country}
                subtitle={d.tagline}
                testId={`style-destination-${d.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}

      {journeys.length > 0 && (
        <Section testId="style-journeys">
          <SectionHead eyebrow="Ready-made" title="Signature journeys in this style" />
          <Grid cols={3}>
            {journeys.map((jj) => (
              <OverlayCard
                key={jj.slug}
                to={`/journeys/${jj.slug}`}
                image={jj.card_image || jj.hero_image}
                title={jj.title}
                meta={`${jj.nights} nights`}
                subtitle={jj.tagline}
                testId={`style-journey-${jj.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- SEASONAL */
export function CollectionDetail() {
  const { slug } = useParams();
  const { doc: c, error } = useDoc("seasonal_collections", slug);
  const [journeys, setJourneys] = useState([]);
  const [dests, setDests] = useState([]);

  useEffect(() => {
    if (!c) return;
    if (c.journey_slugs?.length)
      getList("journeys", { slugs: c.journey_slugs.join(",") })
        .then((r) => setJourneys(r.items || []))
        .catch(() => {});
    if (c.destination_slugs?.length)
      getList("destinations", { slugs: c.destination_slugs.join(",") })
        .then((r) => setDests(r.items || []))
        .catch(() => {});
  }, [c]);

  if (error) return <NotFoundBlock backTo="/collections" label="All collections" />;
  if (!c) return <Loading label="Loading collection" />;

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Collections", to: "/collections" },
    { label: c.title },
  ];

  return (
    <>
      <Seo
        title={c.meta_title || `${c.title} | Azulo Travel`}
        description={c.meta_description || c.overview}
        image={c.og_image || c.hero_image}
        path={`/collections/${c.slug}`}
        record={c}
        breadcrumbs={crumbs}
      />
      <PageHero image={c.hero_image} eyebrow={c.window} title={c.seo_heading || c.title} subtitle={c.tagline} />
      <Section testId="collection-detail">
        <Breadcrumbs items={crumbs} />
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-16">
          <div className="min-w-0">
            <RichText text={c.overview} />
          </div>
          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <InquiryForm source="journey" sourceRef={c.slug} sourceTitle={c.title}
              title={`Enquire — ${c.title}`} />
          </aside>
        </div>
      </Section>
      {journeys.length > 0 && (
        <Section wash testId="collection-journeys">
          <SectionHead eyebrow="In this collection" title="Journeys" />
          <Grid cols={3}>
            {journeys.map((jj) => (
              <OverlayCard
                key={jj.slug}
                to={`/journeys/${jj.slug}`}
                image={jj.card_image || jj.hero_image}
                title={jj.title}
                meta={`${jj.nights} nights`}
                subtitle={jj.tagline}
                testId={`collection-journey-${jj.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}
      {dests.length > 0 && (
        <Section testId="collection-destinations">
          <SectionHead eyebrow="Where to go" title="Destinations" />
          <Grid cols={4}>
            {dests.map((d) => (
              <EditorialCard
                key={d.slug}
                to={destUrl(d)}
                image={d.card_image || d.hero_image}
                title={d.title}
                meta={d.country}
                subtitle={d.tagline}
                testId={`collection-destination-${d.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- VISA */
export function VisaDetail() {
  const { slug } = useParams();
  const { doc: v, error } = useDoc("visa_pages", slug);
  if (error) return <NotFoundBlock backTo="/visa" label="All visa services" />;
  if (!v) return <Loading label="Loading visa guide" />;

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Visa Services", to: "/visa" },
    { label: v.country },
  ];
  const facts = [
    ["Visa type", v.visa_type],
    ["Processing time", v.processing_time],
    ["Validity", v.validity],
    ["Permitted stay", v.stay_duration],
    ["Entry", v.entry],
    ["Government fee", v.govt_fee],
    ["Azulo service fee", v.azulo_fee],
  ];

  return (
    <>
      <Seo
        title={v.meta_title || `${v.title} | Azulo Travel`}
        description={v.meta_description}
        image={v.og_image || v.hero_image}
        path={`/visa/${v.slug}`}
        record={v}
        keywords={v.keywords || []}
        breadcrumbs={crumbs}
        schema={
          v.faqs?.length
            ? {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: v.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.question,
                  acceptedAnswer: { "@type": "Answer", text: f.answer },
                })),
              }
            : null
        }
      />
      <PageHero
        image={v.hero_image}
        eyebrow="Visa Services"
        title={`${v.flag_emoji || ""} ${v.title}`}
        subtitle={v.visa_type}
        height="short"
      />
      <Section testId="visa-detail">
        <Breadcrumbs items={crumbs} />
        <div className="grid gap-12 lg:grid-cols-[1.55fr_1fr] lg:gap-16">
          <div className="min-w-0 space-y-10">
            <div>
              <p className="eyebrow mb-3">Overview</p>
              <RichText text={v.overview} />
            </div>

            <Block eyebrow="At a glance" title="Key facts">
              <dl data-testid="visa-facts" className="overflow-hidden rounded-xl border border-[var(--line)]">
                {facts.map(([label, value], i) =>
                  value ? (
                    <div
                      key={label}
                      className={`flex flex-col gap-1 p-4 sm:flex-row sm:gap-6 ${
                        i % 2 ? "bg-[var(--surface-warm)]" : "bg-[var(--surface)]"
                      }`}
                    >
                      <dt className="eyebrow shrink-0 sm:w-48">{label}</dt>
                      <dd className="text-[0.9rem] text-[var(--ink-2)]">{value}</dd>
                    </div>
                  ) : null
                )}
              </dl>
            </Block>

            {v.documents?.length > 0 && (
              <Block eyebrow="Checklist" title="Documents required">
                <TagList items={v.documents} testId="visa-documents" />
              </Block>
            )}

            {v.process?.length > 0 && (
              <Block eyebrow="How it works" title="The process">
                <ol data-testid="visa-process">
                  {v.process.map((p, i) => (
                    <li key={p.step || p.title || i} className="flex gap-5 border-b border-[var(--line)] py-5 last:border-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(176,141,87,0.14)] text-xs font-bold text-[var(--brass-2)]">
                        {p.step}
                      </span>
                      <div>
                        <h3 className="font-display text-lg text-[var(--ink)]">{p.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-[var(--muted-ink)]">{p.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </Block>
            )}

            {v.notes?.length > 0 && (
              <Block eyebrow="Read this" title="Important notes">
                <ul
                  data-testid="visa-notes"
                  className="space-y-3 rounded-xl border border-[var(--line)] bg-[var(--surface-warm)] p-5"
                >
                  {v.notes.map((n, i) => (
                    <li key={typeof n === "string" ? n : i} className="text-[0.9rem] leading-relaxed text-[var(--ink-2)]">
                      &bull; {n}
                    </li>
                  ))}
                </ul>
              </Block>
            )}

            {v.faqs?.length > 0 && (
              <Block eyebrow="Questions" title="Frequently asked">
                <FaqList faqs={v.faqs} testId="visa-faq" />
              </Block>
            )}
          </div>
          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <InquiryForm
              source="visa"
              sourceRef={v.slug}
              sourceTitle={v.title}
              destination={v.country}
              compact
              title={`Apply for a ${v.country} visa`}
              subtitle="We send your personalised checklist within one business day."
            />
          </aside>
        </div>
      </Section>
    </>
  );
}

/* ---------------------------------------------------------------- BLOG POST */
export function BlogPost() {
  const { slug } = useParams();
  const { doc: b, error } = useDoc("blogs", slug);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (b?.related?.length)
      getList("blogs", { slugs: b.related.join(",") })
        .then((r) => setRelated(r.items || []))
        .catch(() => {});
  }, [b]);

  if (error) return <NotFoundBlock backTo="/blog" label="Back to the journal" />;
  if (!b) return <Loading label="Loading article" />;

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Journal", to: "/blog" },
    { label: b.title },
  ];

  return (
    <>
      <Seo
        title={b.meta_title || `${b.title} | Azulo Travel`}
        description={b.meta_description || b.excerpt}
        image={b.og_image || b.hero_image}
        path={`/blog/${b.slug}`}
        record={b}
        type="article"
        keywords={b.keywords || b.tags || []}
        breadcrumbs={crumbs}
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: b.title,
          description: b.excerpt,
          image: b.hero_image,
          datePublished: b.published_at,
          dateModified: b.updated_at || b.published_at,
          author: { "@type": "Person", name: b.author },
          publisher: { "@type": "Organization", name: "Azulo Travel" },
        }}
      />
      <PageHero
        image={b.hero_image}
        eyebrow={`${b.category} · ${b.read_time}`}
        title={b.title}
        subtitle={b.excerpt}
        height="short"
      />
      <Section testId="blog-post">
        <Breadcrumbs items={crumbs} />
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-16">
          <article className="min-w-0">
            <div className="mb-8 flex items-center gap-3 border-b border-[var(--line)] pb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(176,141,87,0.16)] font-display text-sm text-[var(--brass-2)]">
                {(b.author || "A").charAt(0)}
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--ink)]">{b.author}</p>
                <p className="text-xs text-[var(--muted-ink)]">
                  {b.author_role} &middot; {b.published_at}
                </p>
              </div>
            </div>
            <RichText text={b.body} />
            {b.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2 border-t border-[var(--line)] pt-6">
                {b.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-[var(--paper-2)] px-3 py-1.5 text-xs text-[var(--muted-ink)]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </article>
          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <InquiryForm
              source="contact"
              sourceRef={b.slug}
              sourceTitle={b.title}
              compact
              title="Planning a trip?"
              subtitle="Speak to a designer who has actually been there."
            />
          </aside>
        </div>
      </Section>
      {related.length > 0 && (
        <Section wash testId="related-blogs">
          <SectionHead eyebrow="Keep reading" title="Related articles" />
          <Grid cols={3}>
            {related.map((r) => (
              <EditorialCard
                key={r.slug}
                to={`/blog/${r.slug}`}
                image={r.hero_image}
                title={r.title}
                meta={`${r.category} · ${r.read_time}`}
                subtitle={r.excerpt}
                testId={`related-blog-${r.slug}`}
              />
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- STATIC PAGE */
export function StaticPage({ slug: fixedSlug, corporate = false }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const { doc: p, error } = useDoc("pages", slug);
  const { settings } = useSite();

  if (error) return <NotFoundBlock backTo="/" label="Back to home" />;
  if (!p) return <Loading label="Loading page" />;

  const crumbs = [{ label: "Home", to: "/" }, { label: p.title }];

  return (
    <>
      <Seo
        title={p.meta_title || `${p.title} | Azulo Travel`}
        description={p.meta_description || p.subtitle}
        image={p.og_image || p.hero_image}
        path={`/${p.slug}`}
        record={p}
        keywords={p.keywords || []}
        breadcrumbs={crumbs}
      />
      <PageHero
        image={p.hero_image}
        eyebrow={p.eyebrow}
        title={p.seo_heading || p.title}
        subtitle={p.subtitle}
        height="short"
      />
      <Section testId={`static-page-${p.slug}`}>
        <Breadcrumbs items={crumbs} />
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-16">
          <div className="min-w-0">
            <RichText text={p.body} />
            {p.stats?.length > 0 && (
              <div
                data-testid="page-stats"
                className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4"
              >
                {p.stats.map((s, i) => (
                  <div key={s.label || i} className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-5">
                    <p className="font-display text-2xl text-[var(--brass-2)]">{s.value}</p>
                    <p className="mt-1 text-xs leading-snug text-[var(--muted-ink)]">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <aside className="lg:sticky lg:top-24 lg:h-fit" id="enquire">
            <InquiryForm
              corporate={corporate}
              source={corporate ? "corporate" : "contact"}
              sourceTitle={p.title}
              title={corporate ? "Request a corporate proposal" : "Speak to a travel designer"}
              subtitle={
                corporate
                  ? "We reply within one business day with a service and pricing proposal."
                  : "We reply within one business day."
              }
            />
            {settings.phone && (
              <div className="mt-4 rounded-xl border border-[var(--line)] bg-[var(--surface-warm)] p-5 text-sm">
                <p className="font-semibold text-[var(--ink)]">Prefer to talk?</p>
                <a
                  href={`tel:${settings.phone.replace(/\s/g, "")}`}
                  data-testid="page-phone-link"
                  className="mt-1 block text-[var(--brass-2)]"
                >
                  {settings.phone}
                </a>
                <a
                  href={`mailto:${corporate ? settings.corporate_email : settings.email}`}
                  className="mt-0.5 block text-[var(--brass-2)]"
                >
                  {corporate ? settings.corporate_email : settings.email}
                </a>
                <p className="mt-3 text-xs text-[var(--muted-ink)]">
                  Mon–Sat, 9.30am–7pm IST. Emergency line 24x7 for travellers on a journey.
                </p>
              </div>
            )}
          </aside>
        </div>
      </Section>

      {p.blocks?.length > 0 && (
        <Section wash testId="page-blocks" id={corporate ? "mice" : undefined}>
          <div className="space-y-16 lg:space-y-24">
            {p.blocks.map((blk, i) => (
              <motion.div
                key={blk.heading || blk.title || i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                data-testid={`page-block-${i}`}
                className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-16 ${
                  i % 2 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="az-media overflow-hidden rounded-2xl" style={{ aspectRatio: "4/3" }}>
                  {blk.image && <img src={blk.image} alt={blk.heading} loading="lazy" className="img-cover" />}
                </div>
                <div>
                  <h2 className="font-display text-3xl leading-tight text-[var(--ink)]">{blk.heading}</h2>
                  <p className="mt-4 text-[0.95rem] leading-relaxed text-[var(--muted-ink)]">{blk.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {p.team?.length > 0 && (
        <Section testId="page-team">
          <SectionHead eyebrow="The team" title="Who designs your journey" />
          <Grid cols={3}>
            {p.team.map((m, i) => (
              <motion.div key={m.name || i} variants={fadeUp} className="az-card" data-testid={`team-member-${i}`}>
                {m.image ? (
                  <Img src={m.image} alt={m.name} ratio="4/3" />
                ) : (
                  <div className="flex items-center gap-3 px-5 pt-5">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[var(--brass-2)]/10 font-display text-lg text-[var(--brass-2)]">
                      {m.name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-display text-xl text-[var(--ink)]">{m.name}</h3>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brass-2)]">
                    {m.role}
                  </p>
                  <p className="mt-2.5 text-sm leading-relaxed text-[var(--muted-ink)]">{m.bio}</p>
                </div>
              </motion.div>
            ))}
          </Grid>
        </Section>
      )}

      {p.faqs?.length > 0 && (
        <Section wash testId="page-faqs">
          <div className="mx-auto max-w-3xl">
            <SectionHead eyebrow="Questions" title="Frequently asked" align="center" />
            <FaqList faqs={p.faqs} testId="page-faq-accordion" />
          </div>
        </Section>
      )}
    </>
  );
}
